import React, { useState } from 'react';
import { Form, Input, InputNumber, Row, Col, Button, Card, Radio, Space, Typography, Divider } from 'antd';
import { RightOutlined, ThunderboltOutlined } from '@ant-design/icons';

const { Text } = Typography;

const Step3Bateria = ({ onNext, onBack, formData }) => {
    const [form] = Form.useForm();
    const [hasBattery, setHasBattery] = useState(formData?.hasBattery ?? false);
    const [sameAddress, setSameAddress] = useState(formData?.bateriaSameAddress ?? true);

    React.useEffect(() => {
        if (formData) {
            form.setFieldsValue(formData);
            if (formData.hasBattery !== undefined) setHasBattery(formData.hasBattery);
            if (formData.bateriaSameAddress !== undefined) setSameAddress(formData.bateriaSameAddress);
        }
    }, [formData, form]);

    const handleSubmit = async (values) => {
        const data = {
            ...values,
            hasBattery,
            bateriaSameAddress: hasBattery ? sameAddress : undefined,
        };
        onNext(data);
    };

    return (
        <Card title={
            <span>
                <ThunderboltOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
                Banco de Baterias
            </span>
        }>
            <Form
                form={form}
                layout="vertical"
                onFinish={handleSubmit}
                initialValues={formData}
            >
                <Form.Item label="Este site possui banco de baterias?">
                    <Radio.Group 
                        value={hasBattery} 
                        onChange={(e) => setHasBattery(e.target.value)}
                        buttonStyle="solid"
                    >
                        <Radio.Button value={true}>Sim, possui bateria</Radio.Button>
                        <Radio.Button value={false}>Não possui bateria</Radio.Button>
                    </Radio.Group>
                </Form.Item>

                {hasBattery && (
                    <div style={{ padding: '20px', backgroundColor: '#e6f7ff', borderRadius: '8px' }}>
                        <Text strong style={{ fontSize: '16px' }}>Informações do Banco de Baterias</Text>
                        <Divider style={{ margin: '16px 0' }} />

                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item
                                    label="Capacidade Total (kWh)"
                                    name="batteryCapacity"
                                    rules={[{ required: hasBattery, message: 'Capacidade é obrigatória' }]}
                                >
                                    <InputNumber 
                                        min={0} 
                                        step={0.1} 
                                        style={{ width: '100%' }} 
                                        placeholder="100.0"
                                        precision={1}
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    label="Fabricante"
                                    name="bateriaFabricante"
                                >
                                    <Input placeholder="Ex: BYD, LG Chem, Tesla" />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Divider orientation="left">Responsável pelo Gerenciamento</Divider>

                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item
                                    label="Nome do Gerente"
                                    name="bateriaGerenteNome"
                                    rules={[{ required: hasBattery, message: 'Nome do gerente é obrigatório' }]}
                                >
                                    <Input placeholder="João Silva" />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    label="Email do Gerente"
                                    name="bateriaGerenteEmail"
                                    rules={[
                                        { required: hasBattery, message: 'Email do gerente é obrigatório' },
                                        { type: 'email', message: 'Email inválido' }
                                    ]}
                                >
                                    <Input placeholder="joao@empresa.com" />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Form.Item label="O endereço da bateria é o mesmo do site?">
                            <Radio.Group 
                                value={sameAddress} 
                                onChange={(e) => setSameAddress(e.target.value)}
                            >
                                <Radio value={true}>Sim, mesmo endereço</Radio>
                                <Radio value={false}>Não, endereço diferente</Radio>
                            </Radio.Group>
                        </Form.Item>

                        {!sameAddress && (
                            <div style={{ padding: '16px', backgroundColor: '#fff', borderRadius: '4px', border: '1px solid #d9d9d9' }}>
                                <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '12px' }}>
                                    Preencha o endereço específico do banco de baterias:
                                </Text>
                                
                                <Row gutter={16}>
                                    <Col span={16}>
                                        <Form.Item
                                            label="Logradouro"
                                            name="bateriaLogradouro"
                                            rules={[{ required: !sameAddress, message: 'Logradouro é obrigatório' }]}
                                        >
                                            <Input placeholder="Rua, Avenida, etc." />
                                        </Form.Item>
                                    </Col>
                                    <Col span={8}>
                                        <Form.Item
                                            label="Número"
                                            name="bateriaNumero"
                                            rules={[{ required: !sameAddress, message: 'Número é obrigatório' }]}
                                        >
                                            <Input placeholder="123" />
                                        </Form.Item>
                                    </Col>
                                </Row>

                                <Row gutter={16}>
                                    <Col span={8}>
                                        <Form.Item
                                            label="CEP"
                                            name="bateriaCep"
                                            rules={[{ required: !sameAddress, message: 'CEP é obrigatório' }]}
                                        >
                                            <Input placeholder="01234-567" />
                                        </Form.Item>
                                    </Col>
                                    <Col span={8}>
                                        <Form.Item
                                            label="Bairro"
                                            name="bateriaBairro"
                                            rules={[{ required: !sameAddress, message: 'Bairro é obrigatório' }]}
                                        >
                                            <Input placeholder="Centro" />
                                        </Form.Item>
                                    </Col>
                                    <Col span={8}>
                                        <Form.Item
                                            label="Cidade"
                                            name="bateriaCidade"
                                            rules={[{ required: !sameAddress, message: 'Cidade é obrigatória' }]}
                                        >
                                            <Input placeholder="São Paulo" />
                                        </Form.Item>
                                    </Col>
                                </Row>
                            </div>
                        )}
                    </div>
                )}

                <Form.Item style={{ marginBottom: 0, marginTop: '24px', textAlign: 'right' }}>
                    <Space>
                        <Button onClick={onBack}>
                            Voltar
                        </Button>
                        <Button 
                            type="primary" 
                            htmlType="submit"
                            icon={<RightOutlined />}
                            size="large"
                            style={{ backgroundColor: '#FB8500', borderColor: '#FB8500' }}
                        >
                            Próximo: Grid
                        </Button>
                    </Space>
                </Form.Item>
            </Form>
        </Card>
    );
};

export default Step3Bateria;
